/**
 * Simple type-safe event bus for cross-component communication
 * Used to notify components when data changes (e.g., AI creates a task)
 */
import { logger } from './logger'

type EventCallback = () => void

export type EventType =
  | 'tasks:updated'
  | 'tasks:openForm'
  | 'tasks:openEditForm'
  | 'deepWork:openForm'
  | 'deepWork:updated'
  | 'habits:updated'
  | 'tracking:updated'
  | 'layout:updated'
  | 'workflows:updated'
  | 'chat:newConversation'
  | 'notes:createNote'
  | 'projects:createProject'

/**
 * Module-level storage for pending edit task ID
 * Set by CommandPalette, read by TaskList when 'tasks:openEditForm' fires
 */
let pendingEditTaskId: number | null = null

export const setPendingEditTaskId = (id: number | null) => {
  pendingEditTaskId = id
}

export const getPendingEditTaskId = (): number | null => {
  return pendingEditTaskId
}

class EventBus {
  private listeners = new Map<EventType, Set<EventCallback>>()

  /**
   * Subscribe to an event
   */
  on(event: EventType, callback: EventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)

    // Return unsubscribe function
    return () => this.off(event, callback)
  }

  /**
   * Unsubscribe from an event
   */
  off(event: EventType, callback: EventCallback): void {
    this.listeners.get(event)?.delete(callback)
  }

  /**
   * Emit an event to all subscribers
   */
  emit(event: EventType): void {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback()
      } catch (error) {
        logger.error(`Error in event listener for ${event}`, { error })
      }
    })
  }

  /**
   * Clear all listeners (useful for testing)
   */
  clear(): void {
    this.listeners.clear()
  }
}

// Export singleton instance
export const eventBus = new EventBus()
